import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Panel, PageHeader, Chip } from "@/components/kit";
import { Warehouse, Boxes, ChevronRight, ChevronDown, Loader2, MapPin } from "lucide-react";

function BinRow({ bin, stock }) {
  const [open, setOpen] = useState(false);
  const s = stock || { on_hand: 0, reserved: 0, lines: [] };
  return (
    <div className="border border-slate-800 rounded-md">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`bin-${bin.code}`}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-800/40 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm text-slate-200">
          {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
          <MapPin className="h-3.5 w-3.5 text-slate-500" /> {bin.code}
          {bin.name && <span className="text-slate-500 text-xs">· {bin.name}</span>}
        </span>
        <Chip tone={s.on_hand > 0 ? "emerald" : "slate"}>{s.on_hand.toLocaleString()} on hand</Chip>
      </button>
      {open && (
        <div className="px-3 pb-3">
          {s.lines.length === 0 ? (
            <div className="text-xs text-slate-600 font-mono py-2">No stock in this location</div>
          ) : (
            <table className="w-full text-xs mt-1">
              <thead><tr className="text-left font-mono uppercase tracking-wider text-slate-600">
                <th className="py-1">SKU</th><th className="py-1">Item</th><th className="py-1 text-right">On hand</th><th className="py-1 text-right">Reserved</th>
              </tr></thead>
              <tbody>
                {s.lines.map((l, i) => (
                  <tr key={i} className="border-t border-slate-800/60">
                    <td className="py-1 font-mono text-slate-400">{l.sku}</td>
                    <td className="py-1 text-slate-300">{l.name}</td>
                    <td className="py-1 text-right font-mono text-emerald-400">{l.quantity_on_hand.toLocaleString()}</td>
                    <td className="py-1 text-right font-mono text-amber-400">{l.quantity_reserved.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default function Warehouses() {
  const [warehouses, setWarehouses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/warehouses").then((r) => {
      setWarehouses(r.data);
      if (r.data[0]) setSelected(r.data[0].id);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setTree(null);
    api.get(`/warehouses/${selected}/locations`).then((r) => setTree(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [selected]);

  const racksInZone = (zid) => (tree?.racks || []).filter((r) => r.zone_id === zid);
  const binsInZoneNoRack = (zid) => (tree?.bins || []).filter((b) => b.zone_id === zid && !b.rack_id);
  const binsInRack = (rid) => (tree?.bins || []).filter((b) => b.rack_id === rid);

  return (
    <div className="space-y-6" data-testid="warehouses-page">
      <PageHeader title="Warehouses" subtitle="Browse warehouse topology with live stock per storage location." />
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        <div className="space-y-3">
          {loading ? <Loader2 className="h-6 w-6 animate-spin text-orange-500" /> :
            warehouses.map((w) => (
              <button
                key={w.id}
                onClick={() => setSelected(w.id)}
                data-testid={`warehouse-card-${w.code}`}
                className={`w-full text-left p-4 rounded-md border transition-all ${
                  selected === w.id ? "border-orange-500/50 bg-orange-500/5" : "border-slate-800 bg-slate-900/60 hover:border-slate-600"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 font-semibold text-slate-100"><Warehouse className="h-4 w-4 text-orange-400" /> {w.name}</span>
                  <Chip>{w.code}</Chip>
                </div>
                <div className="flex gap-4 mt-3 font-mono text-xs text-slate-500">
                  <span>{w.zone_count} zones</span><span>{w.bin_count} bins</span>
                  <span className="ml-auto text-emerald-400">{w.total_on_hand.toLocaleString()} on hand</span>
                </div>
              </button>
            ))}
        </div>

        <Panel className="p-5">
          {!tree ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
          ) : (
            <div className="space-y-5" data-testid="warehouse-tree">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Boxes className="h-4 w-4 text-orange-400" />
                <span className="font-semibold">{tree.warehouse.name}</span>
                <span className="text-slate-500 font-mono text-xs">/ {tree.zones.length} zones</span>
              </div>
              {tree.zones.length === 0 && <div className="text-sm text-slate-500">No zones defined.</div>}
              {tree.zones.map((z) => (
                <div key={z.id} className="border-l-2 border-slate-800 pl-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="font-heading uppercase tracking-wide text-slate-200">{z.name}</span>
                    <Chip>{z.code}</Chip><Chip tone="slate">{z.zone_type}</Chip>
                  </div>
                  {racksInZone(z.id).map((rk) => (
                    <div key={rk.id} className="pl-3 space-y-2">
                      <div className="font-mono text-xs uppercase tracking-wider text-slate-500">Rack {rk.code}</div>
                      <div className="space-y-2">{binsInRack(rk.id).map((b) => <BinRow key={b.id} bin={b} stock={tree.stock_by_bin[b.id]} />)}</div>
                    </div>
                  ))}
                  {binsInZoneNoRack(z.id).length > 0 && (
                    <div className="space-y-2">{binsInZoneNoRack(z.id).map((b) => <BinRow key={b.id} bin={b} stock={tree.stock_by_bin[b.id]} />)}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
